import {
  getResource,
  Resource,
} from "@redotech/redo-model/localization/resource";
import {
  BlueYonderZod,
  PickupLocation,
  ReturnBearZod,
  ReturnWarning,
} from "@redotech/redo-model/return";
import { pickupLocationOptions } from "@redotech/redo-model/return-flow";
import { ReturnStatus } from "@redotech/redo-model/return-status";
import { Trackable, TrackableType } from "@redotech/redo-model/trackable";
import { CustomerPortalAppSettings } from "../../settings";
import { isInternationalReturn } from "../../util";

export enum StepTypeEnum {
  NONE = "none",
  EXCHANGE_ORDER = "exchange_order",
  MANUAL_REVIEW = "manual_review",
  GREEN_RETURN = "green_return",
  IN_TRANSIT = "in_transit",
  DELIVERED = "delivered",
  PROCESSED = "processed",
  WARNING = "warning",
}

export enum StepIdentifierEnum {
  NONE = "none",
  MANUAL_REVIEW = "manual-review",
  EXCHANGE_ORDER = "exchange-order",
  IN_STORE_PRINT_PACKING_SLIP = "in-store-print-packing-slip",
  IN_STORE_RETURN = "in-store-return",
  IN_STORE_PROCESSED = "in-store-processed",
  HAPPY_RETURNS_DROP_OFF = "happy-returns-drop-off",
  HAPPY_RETURNS_PROCESSED = "happy-returns-processed",
  BLUE_YONDER_DROP_OFF = "blue-yonder-drop-off",
  RETURN_BEAR_SHIPPING = "return-bear-shipping",
  SHIPMENT_TRANSIT = "shipment-transit",
  PICKUP_PRINT_LABEL = "pickup-print-label",
  PICKUP_ATTACH_LABEL = "pickup-attach-label",
  PICKUP_MANAGE = "pickup-manage",
  DROP_OFF_PACKAGE = "drop-off-package",
  PROCESSED = "processed",
  PROCESSED_REPAIR = "processed-repair",
  SHIP_BACK_REPAIR = "ship-back-repair",
  GREEN_RETURN_INSTRUCTIONS = "green-return-instructions",
  GREEN_RETURN_PROCESSED = "green-return-processed",
  WARNING = "warning",
}

export type StepType =
  | ({ type: StepIdentifierEnum.NONE; stepType: StepTypeEnum.NONE } & Step)
  | ({
      type: StepIdentifierEnum.MANUAL_REVIEW;
      stepType: StepTypeEnum.MANUAL_REVIEW;
    } & Step)
  | ({
      type: StepIdentifierEnum.EXCHANGE_ORDER;
      stepType: StepTypeEnum.EXCHANGE_ORDER;
    } & Step)
  | ({
      type: StepIdentifierEnum.IN_STORE_PRINT_PACKING_SLIP;
      stepType: StepTypeEnum.IN_TRANSIT;
      packingSlipLink?: string;
    } & Step)
  | ({
      type: StepIdentifierEnum.IN_STORE_RETURN;
      stepType: StepTypeEnum.IN_TRANSIT;
    } & Step)
  | ({
      type: StepIdentifierEnum.IN_STORE_PROCESSED;
      stepType: StepTypeEnum.PROCESSED;
    } & Step)
  | ({
      type: StepIdentifierEnum.HAPPY_RETURNS_DROP_OFF;
      happyReturnsData: any;
      zipCode: string | undefined | null;
      stepType: StepTypeEnum.IN_TRANSIT;
      //todo
    } & Step)
  | ({
      type: StepIdentifierEnum.HAPPY_RETURNS_PROCESSED;
      stepType: StepTypeEnum.PROCESSED;
    } & Step)
  | ({
      type: StepIdentifierEnum.BLUE_YONDER_DROP_OFF;
      blueYonderQRCodes: BlueYonderZod["returns"];
      stepType: StepTypeEnum.IN_TRANSIT;
    } & Step)
  | ({
      type: StepIdentifierEnum.RETURN_BEAR_SHIPPING;
      returnBearDetails: ReturnBearZod;
      stepType: StepTypeEnum.IN_TRANSIT;
    } & Step)
  | ({
      type: StepIdentifierEnum.SHIPMENT_TRANSIT;
      shipmentInfo?: {
        carrier: string | undefined;
        commercialInvoice: string | undefined | null;
        trackable: Trackable & { type: TrackableType.RETURN };
      };
      stepType: StepTypeEnum.IN_TRANSIT;
    } & Step)
  | ({
      type: StepIdentifierEnum.PICKUP_PRINT_LABEL;
      shipmentInfo?: {
        carrier: string | undefined;
        commercialInvoice: string | undefined | null;
        trackable: Trackable & { type: TrackableType.RETURN };
      };
      stepType: StepTypeEnum.IN_TRANSIT;
    } & Step)
  | ({
      type: StepIdentifierEnum.PICKUP_ATTACH_LABEL;
      stepType: StepTypeEnum.IN_TRANSIT;
    } & Step)
  | ({
      type: StepIdentifierEnum.PICKUP_MANAGE;
      stepType: StepTypeEnum.IN_TRANSIT;
    } & Step)
  | ({
      type: StepIdentifierEnum.DROP_OFF_PACKAGE;
      trackable: Trackable & { type: TrackableType.RETURN };
      stepType: StepTypeEnum.IN_TRANSIT;
    } & Step)
  | ({
      type: StepIdentifierEnum.PROCESSED;
      stepType: StepTypeEnum.PROCESSED;
    } & Step)
  | ({
      type: StepIdentifierEnum.PROCESSED_REPAIR;
      stepType: StepTypeEnum.PROCESSED;
    } & Step)
  | ({
      type: StepIdentifierEnum.SHIP_BACK_REPAIR;
      trackable: Trackable;
      stepType: StepTypeEnum.PROCESSED;
    } & Step)
  | ({
      type: StepIdentifierEnum.GREEN_RETURN_INSTRUCTIONS;
      stepType: StepTypeEnum.GREEN_RETURN;
    } & Step)
  | ({
      type: StepIdentifierEnum.GREEN_RETURN_PROCESSED;
      stepType: StepTypeEnum.PROCESSED;
    } & Step)
  | ({
      type: StepIdentifierEnum.WARNING;
      stepType: StepTypeEnum.WARNING;
    } & Step);

export type Step = {
  stepType: StepTypeEnum;
  title: string;
  subtitle: string;
  complete: boolean;
};
export interface UseStepsResponse {
  steps: StepType[] | undefined;
  stepResource: Resource | undefined;
}

export function useSteps({
  settings,
  trackable,
}: {
  settings: CustomerPortalAppSettings | undefined;
  trackable: Trackable | undefined;
}): UseStepsResponse {
  //Someone refactor this, I don't have time

  const steps: StepType[] = [];
  if (trackable?.type !== TrackableType.RETURN || !settings) {
    return { steps: undefined, stepResource: undefined };
  }
  const isRepair = trackable.returnProducts.some(
    (product: any) => product.strategy === "repair",
  );

  const isInternational = isInternationalReturn(
    trackable.originalOrder.shopify.shippingAddress?.country,
    undefined,
    settings,
  );

  const isBuyYourOwnLabel =
    settings.returns?.buyYourOwnLabel &&
    (isInternational || settings.returns?.byolDomestically);

  const hasPickup =
    trackable.pickup && trackable.pickup.status !== "pending_refund";

  const showQRCodeMessage =
    trackable.type === TrackableType.RETURN && !!trackable.form_label;
  const carrier = trackable?.fulfillments[0]?.trackers[0]?.carrier;

  const copyResource = getResource({
    variant: hasPickup ? "pickup" : trackable.returnType,
    overrides: settings.resourceOverride,
    // This is an example of how we could get the language region based on the country code
    // language: getLanguageRegion({
    //   countryCode: returnData.shipping_address?.country_code,
    // }),
  });

  if (trackable?.markedForManualReview && !settings.hideManualReviewStep) {
    steps.push({
      type: StepIdentifierEnum.MANUAL_REVIEW,
      title: copyResource.STATUS_MANUAL_REVIEW_TITLE,
      subtitle: trackable.returnProducts.some(
        (product) => !product.green_return,
      )
        ? copyResource.STATUS_MANUAL_REVIEW_SUBTITLE
        : "",
      complete: false,
      stepType: StepTypeEnum.MANUAL_REVIEW,
    });
  }

  if (trackable.draftOrderURL) {
    let subtitle = "";
    if (settings.exchanges.createLabelOnDraftOrderComplete) {
      subtitle = "You'll receive your label after you complete your order";
    }
    steps.push({
      type: StepIdentifierEnum.EXCHANGE_ORDER,
      title: "Pay for exchange order",
      subtitle,
      complete: false,
      stepType: StepTypeEnum.EXCHANGE_ORDER,
    });
  }
  // If there are no label warnings
  if (
    !trackable.warnings?.some(
      (warning: ReturnWarning) => warning.type === "label",
    )
  ) {
    if (trackable.inStoreReturn) {
      const hasPackingSlip =
        settings.inStoreDropOffPackingSlipEnabled && trackable.packingSlipLink;
      if (hasPackingSlip) {
        steps.push({
          type: StepIdentifierEnum.IN_STORE_PRINT_PACKING_SLIP,
          title: "Print off return authorization",
          subtitle: "Click the button to print the return authorization",
          complete: false,
          stepType: StepTypeEnum.IN_TRANSIT,
          packingSlipLink: trackable.packingSlipLink,
        });
      }
      steps.push({
        type: StepIdentifierEnum.IN_STORE_RETURN,
        title: "Return in-store",
        subtitle: "You can bring your items to the store",
        complete: false,
        stepType: StepTypeEnum.IN_TRANSIT,
      });
      steps.push({
        type: StepIdentifierEnum.IN_STORE_PROCESSED,
        title: "We process the return",
        subtitle: "",
        complete: false,
        stepType: StepTypeEnum.PROCESSED,
      });
    } else if (trackable.happyReturnsData) {
      steps.push({
        type: StepIdentifierEnum.HAPPY_RETURNS_DROP_OFF,
        title: "Drop off your items",
        subtitle: "You can drop off your items at any Happy Returns® location",
        happyReturnsData: trackable.happyReturnsData,
        zipCode: trackable.shipping_address?.zip,
        stepType: StepTypeEnum.IN_TRANSIT,
        complete: false,
      });
      steps.push({
        type: StepIdentifierEnum.HAPPY_RETURNS_PROCESSED,
        title: copyResource.STATUS_PROCESSED_TITLE,
        subtitle: copyResource.STATUS_PROCESSED_SUBTITLE,
        complete: false,
        stepType: StepTypeEnum.PROCESSED,
      });
    } else if (trackable.blueYonder?.returns?.length) {
      steps.push({
        type: StepIdentifierEnum.BLUE_YONDER_DROP_OFF,
        title: "Drop off your items (no packaging required)",
        subtitle:
          "You can drop off your items at any BlueYonder-affiliated location, just show this QR code",
        blueYonderQRCodes: trackable.blueYonder.returns,
        stepType: StepTypeEnum.IN_TRANSIT,
        complete: false,
      });
      steps.push({
        type: StepIdentifierEnum.HAPPY_RETURNS_PROCESSED, // Note: This is a shared step type used for both HappyReturns and BlueYonder returns (same processing UI/messaging)
        title: copyResource.STATUS_PROCESSED_TITLE,
        subtitle: copyResource.STATUS_PROCESSED_SUBTITLE,
        complete: false,
        stepType: StepTypeEnum.PROCESSED,
      });
    } else if (trackable.returnBear) {
      const title = trackable.returnBear.qrCodeUri
        ? "Drop off your items (no packaging required)"
        : "Pack up your return";
      const subtitle = trackable.returnBear.qrCodeUri
        ? "You can drop off your items at any Return Bear location, just show this QR code"
        : "Print your shipping label";

      steps.push({
        type: StepIdentifierEnum.RETURN_BEAR_SHIPPING,
        title,
        subtitle,
        returnBearDetails: trackable.returnBear,
        stepType: StepTypeEnum.IN_TRANSIT,
        complete: false,
      });
    } else if (
      trackable.returnProducts.some((product) => !product.green_return)
    ) {
      const commercialInvoice = trackable.shipment?._shipment?.forms?.find(
        (form: any) => form.form_type === "commercial_invoice",
      )?.form_url;
      const singleShipment = !(
        trackable.shipments && trackable.shipments.length > 1
      );

      steps.push({
        type: StepIdentifierEnum.SHIPMENT_TRANSIT,
        title: copyResource.STATUS_IN_TRANSIT_TITLE,
        subtitle: singleShipment
          ? copyResource.STATUS_IN_TRANSIT_SUBTITLE +
            (showQRCodeMessage
              ? " " + copyResource.STATUS_IN_TRANSIT_QR_CODE
              : "")
          : copyResource.STATUS_IN_TRANSIT_MULTI_SHIPMENT_SUBTITLE,
        stepType: StepTypeEnum.IN_TRANSIT,
        shipmentInfo:
          !!trackable.shipments && !hasPickup
            ? { carrier, commercialInvoice, trackable }
            : undefined,

        complete: false,
      });
      if (hasPickup) {
        steps.push({
          type: StepIdentifierEnum.PICKUP_PRINT_LABEL,
          title: "Print your shipping label",
          subtitle: "Click the button to print your shipping label",
          shipmentInfo:
            !!trackable.shipment && singleShipment
              ? { carrier, commercialInvoice, trackable }
              : undefined,

          complete: false,
          stepType: StepTypeEnum.IN_TRANSIT,
        });
        steps.push({
          type: StepIdentifierEnum.PICKUP_ATTACH_LABEL,
          title: "Attach shipping label",
          subtitle:
            "Place label on outside of the package and make sure barcode is visible",
          complete: false,
          stepType: StepTypeEnum.IN_TRANSIT,
        });

        // SINCE "hasPickup" is true, we know that trackable.pickup is not undefined
        // but typescript doesn't know that, so we need to use the nullish coalescing operator
        // ideally it should never fallback to PickupLocation.OTHER, but just in case
        const pickupOption = pickupLocationOptions.get(
          trackable.pickup?.pickupLocation?.packageLocation ??
            PickupLocation.OTHER,
        );

        const pickupLocationLabel =
          pickupOption?.stepLabel ?? "specified location";

        const pickupDate = Temporal.PlainDate.from(
          trackable.pickup?.pickupDate ?? new Date().toISOString(),
        );
        const pickupTitle = `Package pickup on ${pickupDate.toLocaleString(
          "en-US",
          { weekday: "short", month: "short", day: "numeric", year: "numeric" },
        )}`;
        const pickupSubtitle = `Leave your package on your ${pickupLocationLabel} and it will be picked up for you.`;

        steps.push({
          type: StepIdentifierEnum.PICKUP_MANAGE,
          title: pickupTitle,
          subtitle: pickupSubtitle,
          stepType: StepTypeEnum.IN_TRANSIT,
          complete: false,
        });
      } else {
        steps.push({
          type: StepIdentifierEnum.DROP_OFF_PACKAGE,
          title: carrier
            ? `${copyResource.DROP_OFF_PACKAGE_TEXT} at ${carrier}`
            : copyResource.DROP_OFF_PACKAGE_TEXT,
          subtitle: carrier
            ? `${copyResource.DROP_OFF_PACKAGE_SUBTEXT} ${carrier} location`
            : "",
          complete: false,
          trackable,
          stepType: StepTypeEnum.IN_TRANSIT,
        });
      }
      steps.push({
        type: StepIdentifierEnum.PROCESSED,
        title: copyResource.STATUS_PROCESSED_TITLE,
        subtitle: copyResource.STATUS_PROCESSED_SUBTITLE,
        complete: false,
        stepType: StepTypeEnum.PROCESSED,
      });
      if (isRepair) {
        steps.push({
          type: StepIdentifierEnum.PROCESSED_REPAIR,
          title: "We process the repair",
          subtitle:
            "If the item(s) is not repairable, we will replace or refund the item",
          complete: false,
          stepType: StepTypeEnum.PROCESSED,
        });
        steps.push({
          type: StepIdentifierEnum.SHIP_BACK_REPAIR,
          title: "We ship the repaired item",
          subtitle: "",
          complete: false,
          trackable,
          stepType: StepTypeEnum.PROCESSED,
        });
      }
    } else {
      steps.push(
        {
          type: StepIdentifierEnum.GREEN_RETURN_INSTRUCTIONS,
          title:
            settings.theme.green_return_confirmation_text ||
            (isBuyYourOwnLabel
              ? "Please visit a local carrier to purchase a label and get your items shipped"
              : "Keep your items"),
          subtitle:
            settings.theme.green_return_confirmation_description ||
            (isBuyYourOwnLabel
              ? `Our address is: \n${trackable.merchant_address?.name}\n${trackable.merchant_address?.address1} ${trackable.merchant_address?.address2}\n${trackable.merchant_address?.city}, ${trackable.merchant_address?.province} ${trackable.merchant_address?.zip}\n${trackable.merchant_address?.country}`
              : "You do not need to send your items back."),
          complete: false,
          stepType: StepTypeEnum.GREEN_RETURN,
        },
        {
          type: StepIdentifierEnum.GREEN_RETURN_PROCESSED,
          title: copyResource.GREEN_RETURN_PROCESS_STEP_TEXT,
          subtitle: "",
          complete: false,
          stepType: StepTypeEnum.PROCESSED,
        },
      );
    }
  } else {
    // If there are label warnings, we need to let them know that we are working on it
    steps.push({
      type: StepIdentifierEnum.WARNING,
      title: "We're working on it",
      subtitle: "We will resolve any issues and email you with our solution",
      complete: false,
      stepType: StepTypeEnum.WARNING,
    });
  }

  checkStepsCompletion(trackable, steps);
  return { steps, stepResource: copyResource };
}

const checkStepsCompletion = (
  trackable: Trackable & { type: TrackableType.RETURN },
  steps: Step[],
) => {
  // Go through steps array backwards. If the step is complete,
  // mark the rest of the steps as complete.
  let isComplete = false;
  for (let i = steps.length - 1; i >= 0; i--) {
    const step = steps[i];
    if (!step) {
      continue;
    }
    if (isComplete) {
      step.complete = true;
      continue;
    }
    switch (step.stepType) {
      case StepTypeEnum.PROCESSED:
        step.complete = trackable.status === ReturnStatus.COMPLETE;
        break;
      case StepTypeEnum.DELIVERED:
        step.complete = [
          ReturnStatus.COMPLETE,
          ReturnStatus.DELIVERED,
        ].includes(trackable.status);
        break;
      case StepTypeEnum.IN_TRANSIT:
        step.complete = [
          ReturnStatus.COMPLETE,
          ReturnStatus.DELIVERED,
          ReturnStatus.IN_TRANSIT,
        ].includes(trackable.status);
        break;
      case StepTypeEnum.EXCHANGE_ORDER:
        step.complete = trackable.exchangeOrder?.length > 0;
        break;
      case StepTypeEnum.MANUAL_REVIEW:
      case StepTypeEnum.GREEN_RETURN:
        step.complete = trackable.status !== ReturnStatus.NEEDS_REVIEW;
        break;
      case StepTypeEnum.NONE:
        continue;
    }
    isComplete = step.complete;
  }
};
